import React, { useContext, useReducer, useEffect } from 'react'

import { getDefaultEmailVariables } from '../../../../gql'
import {
  formatEmailTemplateData,
  formatNotificationAndSmsData,
  isScheduledTrigger,
  actionTypesByName,
} from '../../../../utils'

const EditSequenceContext = React.createContext(undefined)

const initialValues = {
  hasChanges: false,
  sequence: undefined,
  stage: undefined,
  initialSequence: undefined,
  defaultDynamicValues: undefined,
  errors: {},
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'setHasChanges': {
      return { ...state, hasChanges: action.payload }
    }
    case 'setSequence': {
      return { ...state, sequence: action.payload }
    }
    case 'setStage': {
      return { ...state, stage: action.payload }
    }
    case 'setErrors': {
      return { ...state, errors: action.payload }
    }
    case 'setInitialSequence': {
      return { ...state, initialSequence: action.payload }
    }
    case 'setDefaultDynamicValues': {
      return { ...state, defaultDynamicValues: action.payload }
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

export const useEditSequenceContext = () => {
  const [editSequenceContextState, editSequenceContextDispatch] = useContext(
    EditSequenceContext,
  )
  const { sequence, errors } = editSequenceContextState

  const createEmptySchedule = () => {
    editSequenceContextDispatch({
      type: 'setSequence',
      payload: {
        ...sequence,
        DateSequenceTemplate: [
          ...sequence.DateSequenceTemplate,
          {
            id: `temp-schedule-${new Date().getTime()}`,
            start_date: null,
          },
        ],
      },
    })
  }

  const createEmptyTrigger = () => {
    editSequenceContextDispatch({
      type: 'setSequence',
      payload: {
        ...sequence,
        Trigger: [
          ...sequence.Trigger,
          {
            id: `temp-trigger-${new Date().getTime()}`,
            target_value: '',
            triggerType: {},
          },
        ],
      },
    })
  }

  const deleteSchedule = id => {
    const index = sequence.DateSequenceTemplate.findIndex(
      schedule => schedule.id === id,
    )
    const scheduleCopy = [...sequence.DateSequenceTemplate]
    scheduleCopy.splice(index, 1)
    editSequenceContextDispatch({
      type: 'setSequence',
      payload: {
        ...sequence,
        DateSequenceTemplate: [...scheduleCopy],
      },
    })
  }

  const deleteTrigger = id => {
    const index = sequence.Trigger.findIndex(trigger => trigger.id === id)
    const triggerCopy = [...sequence.Trigger]
    triggerCopy.splice(index, 1)
    editSequenceContextDispatch({
      type: 'setSequence',
      payload: {
        ...sequence,
        Trigger: [...triggerCopy],
      },
    })
  }

  const updateSchedule = schedule => {
    const modifiedSchedules = sequence.DateSequenceTemplate.reduce(
      (acc, curr) => {
        if (curr.id === schedule.id) {
          acc.push({ ...curr, ...schedule })
        } else {
          acc.push(curr)
        }
        return acc
      },
      [],
    )
    editSequenceContextDispatch({
      type: 'setSequence',
      payload: {
        ...sequence,
        DateSequenceTemplate: [...modifiedSchedules],
      },
    })
  }

  const updateTrigger = trigger => {
    const modifiedTriggers = sequence.Trigger.reduce((acc, curr) => {
      if (curr.id === trigger.id) {
        acc.push({ ...curr, ...trigger })
      } else {
        acc.push(curr)
      }
      return acc
    }, [])
    editSequenceContextDispatch({
      type: 'setSequence',
      payload: {
        ...sequence,
        hasScheduledTrigger: modifiedTriggers.some(trigger =>
          isScheduledTrigger(trigger.triggerType.name),
        ),
        Trigger: [...modifiedTriggers],
      },
    })
  }

  const updateSequence = value => {
    editSequenceContextDispatch({
      type: 'setSequence',
      payload: {
        ...sequence,
        ...value,
      },
    })
  }

  const updateStage = value => {
    editSequenceContextDispatch({
      type: 'setStage',
      payload: value,
    })
  }

  const saveOrUpdateAction = ({ actionId, timeDelay }) => {
    if (actionId) {
      const actionsUpdated = sequence.Action.reduce((acc, curr) => {
        if (curr.id === actionId) {
          if (sequence.hasScheduledTrigger) {
            acc.push({ ...curr, time_before: timeDelay })
          } else {
            acc.push({ ...curr, time_delay: timeDelay })
          }
        } else {
          acc.push(curr)
        }
        return acc
      }, [])
      editSequenceContextDispatch({
        type: 'setSequence',
        payload: {
          ...sequence,
          Action: [...actionsUpdated],
        },
      })
    } else {
      editSequenceContextDispatch({
        type: 'setSequence',
        payload: {
          ...sequence,
          Action: [
            ...sequence.Action,
            {
              id: `temp-action-${new Date().getTime()}`,
              time_delay: sequence.hasScheduledTrigger ? '' : timeDelay,
              time_before: sequence.hasScheduledTrigger ? timeDelay : '',
            },
          ],
        },
      })
    }
  }

  const saveOrUpdateTemplate = ({
    inputsData,
    files,
    deletedFilesIds,
    emailId,
  }) => {
    const {
      name,
      subject,
      sender,
      body,
      cc,
      bcc,
      customDynamicValues,
    } = formatEmailTemplateData(inputsData)

    if (emailId) {
      const index = sequence.Action.findIndex(
        action => action.template.id === emailId,
      )
      const actionToModify = sequence.Action[index]
      const actionsCopy = [...sequence.Action]

      actionsCopy[index].template = {
        ...actionToModify.template,
        name,
        subject,
        sender,
        body: JSON.parse(body),
        cc,
        bcc,
        files,
        deletedFilesIds,
        customDynamicValues,
      }

      editSequenceContextDispatch({
        type: 'setSequence',
        payload: {
          ...sequence,
          Action: [...actionsCopy],
        },
      })
    } else {
      editSequenceContextDispatch({
        type: 'setSequence',
        payload: {
          ...sequence,
          Action: [
            ...sequence.Action,
            {
              actionType: { id: '1', name: 'EMAIL' },
              id: `temp-action-${new Date().getTime()}`,
              template: {
                id: `temp-email-${new Date().getTime()}`,
                name,
                subject,
                sender,
                body: JSON.parse(body),
                cc,
                bcc,
                files,
                customDynamicValues,
              },
            },
          ],
        },
      })
    }
  }

  const saveOrUpdateNotificationAndSms = ({ inputsData, commId, commType }) => {
    const {
      name,
      title,
      body,
      route,
      customDynamicValues,
    } = formatNotificationAndSmsData(inputsData, commType)

    const newTemplateData = {
      name,
      body,
      customDynamicValues,
    }

    if (commType === 'notification') {
      newTemplateData.route = route
      newTemplateData.title = title
    }

    if (commId) {
      const index = sequence.Action.findIndex(
        action => action.template.id === commId,
      )
      const actionToModify = sequence.Action[index]
      const actionsCopy = [...sequence.Action]

      actionsCopy[index].template = {
        ...actionToModify.template,
        ...newTemplateData,
      }

      editSequenceContextDispatch({
        type: 'setSequence',
        payload: {
          ...sequence,
          Action: [...actionsCopy],
        },
      })
    } else {
      editSequenceContextDispatch({
        type: 'setSequence',
        payload: {
          ...sequence,
          Action: [
            ...sequence.Action,
            {
              actionType: {
                id: actionTypesByName[commType],
                name: commType.toUpperCase(),
              },
              id: `temp-action-${new Date().getTime()}`,
              template: {
                id: `temp-${commType}-${new Date().getTime()}`,
                ...newTemplateData,
              },
            },
          ],
        },
      })
    }
  }

  const setHasChanges = value => {
    editSequenceContextDispatch({
      type: 'setHasChanges',
      payload: value,
    })
  }

  const deleteAction = id => {
    const index = sequence.Action.findIndex(action => action.id === id)
    const actionCopy = [...sequence.Action]
    actionCopy.splice(index, 1)
    editSequenceContextDispatch({
      type: 'setSequence',
      payload: {
        ...sequence,
        Action: [...actionCopy],
      },
    })
  }

  const setErrors = value => {
    let newErrors = { ...errors }
    if (newErrors[value.type]) {
      if (value.error) {
        newErrors[value.type] = value.error
      } else {
        delete newErrors[value.type]
      }
    } else if (value.type && value.error) {
      newErrors[value.type] = value.error
    }

    editSequenceContextDispatch({
      type: 'setErrors',
      payload: newErrors,
    })
  }

  const setInitialSequence = value => {
    editSequenceContextDispatch({
      type: 'setInitialSequence',
      payload: value,
    })
  }

  return {
    sequence,
    setInitialSequence,
    createEmptySchedule,
    createEmptyTrigger,
    deleteSchedule,
    deleteTrigger,
    updateSchedule,
    updateTrigger,
    updateSequence,
    updateStage,
    saveOrUpdateTemplate,
    saveOrUpdateNotificationAndSms,
    saveOrUpdateAction,
    deleteAction,
    setHasChanges,
    setErrors,
    editSequenceContextState,
    editSequenceContextDispatch,
  }
}

export const EditSequenceProvider = ({
  children,
  initialValues: initialValuesProp,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialValues,
    ...initialValuesProp,
  })

  useEffect(() => {
    async function fetchData() {
      try {
        const { data } = await getDefaultEmailVariables()
        dispatch({
          type: 'setDefaultDynamicValues',
          payload: JSON.parse(data.getDefaultEmailVariables)
            .defaultDynamicValues,
        })
      } catch (error) {
        console.error(error)
      }
    }
    fetchData()
  }, [])

  return (
    <EditSequenceContext.Provider value={[state, dispatch]}>
      {children}
    </EditSequenceContext.Provider>
  )
}
